import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  BUTTON_SIZE,
  BUTTON_THEME,
  CircularLoader as Loader,
  Select,
} from '@st-operations/uikit/src/v2'

import Header from '_components/header'
import { getPartners, getPushes } from '_services/api-integrations'
import { getTaxonomies } from '_services/taxonomies'
import { getTaxonomyIdSets } from '_services/taxonomy-id-sets'

import {
  LIST_FIELDS,
  NO_TAXONOMY,
  POLL_EVERY_MS,
} from '../taxonomies/taxonomy/api-integrations/constants'
import PushTable from '../taxonomies/taxonomy/api-integrations/push-table'
import '../taxonomies/taxonomy/api-integrations/styles.scss'
import { buildSegmentIdOptions } from '../taxonomies/taxonomy/distribution/segment-id-options'
import Coverage from './coverage'

const BASE = '/taxonomy-api-integrations'

// The page's two views. Which is open is in the address (?view=coverage), so
// it can be linked to and survives a refresh.
const VIEWS = [
  { value: 'pushes', label: 'Pushes', search: '' },
  { value: 'coverage', label: 'Coverage', search: '?view=coverage' },
]

// pushes a page - the pager shows whenever there are any, so it is always
// clear how many there are and where in them you are
const PAGE = 10

// Every push, for every taxonomy and for none, newest first - the sidebar's
// way in. A push started here can be for a taxonomy, or for no taxonomy, with
// its audiences listed by mapping ID. Filtered by taxonomy, by partner and by
// mapping ID.
//
// Beside it, coverage: how much of each taxonomy every partner we scan has.
const AllTaxonomyApiIntegrations = (props) => {
  const view =
    new URLSearchParams(props.location?.search || '').get('view') === 'coverage'
      ? 'coverage'
      : 'pushes'
  const [pushes, setPushes] = useState([])
  const [count, setCount] = useState(0)
  const [skip, setSkip] = useState(0)
  // for partner names, and their words for an action
  const [partners, setPartners] = useState([])
  const [taxonomies, setTaxonomies] = useState([])
  // a taxonomy's id, or NO_TAXONOMY - '' for every one
  const [taxonomy, setTaxonomy] = useState('')
  const [partner, setPartner] = useState('')
  // the custom ID sets, whose spaces a push can be by as well as the built-in
  const [idSets, setIdSets] = useState([])
  const [mappingId, setMappingId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      const params = { sort: 'created_at,-1', limit: PAGE, skip, fields: LIST_FIELDS }
      if (taxonomy) params['taxonomy._id'] = taxonomy
      if (partner) params.partner = partner
      if (mappingId) params.mapping_id = mappingId
      const data = await getPushes(params)
      setPushes(data?.items || [])
      setCount(data?.count || 0)
      setError(null)
    } catch (e) {
      setError(e?.message || 'could not load pushes')
    }
    setLoading(false)
  }, [skip, taxonomy, partner, mappingId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    getPartners()
      .then((data) => setPartners(data?.items || []))
      .catch(() => setPartners([]))
    getTaxonomies({ fields: ['_id', 'name', 'slug'], limit: 1000, sort: 'name,1' })
      .then((data) => setTaxonomies(data?.items || []))
      .catch(() => setTaxonomies([]))
    getTaxonomyIdSets()
      .then((data) => setIdSets(data?.items || []))
      .catch(() => setIdSets([]))
  }, [])

  const taxonomyOptions = [
    { value: '', label: 'Every taxonomy' },
    { value: NO_TAXONOMY, label: 'No taxonomy' },
    ...taxonomies.map((t) => ({ value: t._id, label: t.name })),
  ]
  const partnerOptions = [
    { value: '', label: 'Every partner' },
    ...partners.filter((p) => p.available).map((p) => ({ value: p.slug, label: p.name })),
  ]
  // every space a push can be by - the push form's list - by the ID the
  // table shows, A to Z
  const mappingIdOptions = [
    { value: '', label: 'Every mapping ID' },
    ...[...new Set(buildSegmentIdOptions(idSets).map((option) => option.value))]
      .sort((a, b) => a.localeCompare(b))
      .map((code) => ({ value: code, label: code })),
  ]
  const filtered = Boolean(taxonomy || partner || mappingId)

  // keep the list moving while anything on it is running, so progress is
  // visible without a refresh
  const anyRunning = pushes.some((push) => push.status === 'running')
  useEffect(() => {
    if (!anyRunning) return undefined
    const timer = setInterval(load, POLL_EVERY_MS)
    return () => clearInterval(timer)
  }, [anyRunning, load])

  if (loading) {
    return (
      <div className="api-integrations-loading">
        <Loader />
      </div>
    )
  }

  const shown = pushes.length ? `${skip + 1}–${skip + pushes.length} of ${count}` : `0 of ${count}`

  return (
    <div className="api-integrations">
      <Header
        title="Taxonomy API Integrations"
        actions={[{ children: 'New push', onClick: () => props.history.push(`${BASE}/new`) }]}
      />

      <div className="view-tabs" role="tablist" aria-label="Taxonomy API Integrations">
        <For each="option" of={VIEWS}>
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={view === option.value}
            className={`view-tabs__tab ${view === option.value ? 'is-active' : ''}`}
            onClick={() => props.history.replace({ search: option.search })}
          >
            {option.label}
          </button>
        </For>
      </div>

      <Choose>
        <When condition={view === 'coverage'}>
          <Coverage />
        </When>
        <Otherwise>
          <div className="push-filters">
            <div className="push-filters__field">
              <Select
                useReactSelect
                isSearchable
                name="taxonomy"
                label="Taxonomy"
                value={taxonomyOptions.find((o) => o.value === taxonomy) || ''}
                options={taxonomyOptions}
                onChange={(option, name, val) => {
                  setSkip(0)
                  setTaxonomy(val || '')
                }}
              />
            </div>
            <div className="push-filters__field">
              <Select
                useReactSelect
                name="partner"
                label="Partner"
                value={partnerOptions.find((o) => o.value === partner) || ''}
                options={partnerOptions}
                onChange={(option, name, val) => {
                  setSkip(0)
                  setPartner(val || '')
                }}
              />
            </div>
            <div className="push-filters__field">
              <Select
                useReactSelect
                isSearchable
                name="mapping_id"
                label="Mapping ID"
                value={mappingIdOptions.find((o) => o.value === mappingId) || ''}
                options={mappingIdOptions}
                onChange={(option, name, val) => {
                  setSkip(0)
                  setMappingId(val || '')
                }}
              />
            </div>
          </div>

          <If condition={error}>
            <div className="text-red-600 mb-4">{error}</div>
          </If>

          <If condition={!pushes.length && !error}>
            <div className="text-gray-500">
              {filtered ? 'No pushes match these filters.' : 'Nothing has been pushed yet.'}
            </div>
          </If>

          <If condition={pushes.length}>
            <PushTable
              pushes={pushes}
              partners={partners}
              linkTo={(push) => `${BASE}/${push._id}`}
              showTaxonomy
            />
          </If>

          <If condition={count > 0}>
            <div className="pager pager--list">
              <span className="meta">{shown}</span>
              <div className="flex gap-2">
                <Button
                  theme={BUTTON_THEME.SECONDARY}
                  size={BUTTON_SIZE.SMALL}
                  onClick={() => setSkip(Math.max(0, skip - PAGE))}
                  disabled={skip === 0}
                >
                  Previous
                </Button>
                <Button
                  theme={BUTTON_THEME.SECONDARY}
                  size={BUTTON_SIZE.SMALL}
                  onClick={() => setSkip(skip + PAGE)}
                  disabled={skip + PAGE >= count}
                >
                  Next
                </Button>
              </div>
            </div>
          </If>
        </Otherwise>
      </Choose>
    </div>
  )
}

export default AllTaxonomyApiIntegrations
